import React, { Fragment, useState, useEffect } from "react";
import { useEffectOnce } from "../../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessing,
  loadPage,
  setPageValue,
  setPanes,
  deleteEntity,
  entityDeleted,
  disposePage,
  importDatasetId,
} from "../../../redux/slices/datasetslocatorpageslice";
import { addNotification } from "../../../redux/slices/notificationsslice";
import {
  hideAppDialog,
  setAppDialog,
} from "../../../redux/slices/appdialogslice";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import { useNavigate, NavLink } from "react-router-dom";
import { Splitter } from "@progress/kendo-react-layout";
import { Page } from "../../page";
import { Loader } from "@progress/kendo-react-indicators";
import { Input } from "@progress/kendo-react-inputs";
import { notificationDurationMiddle } from "../../../lib/utils";
import { ReactComponent as ImportIcon } from "../../../icons/import.svg";

import "./datasetslocatorpage.css";
import { DatasetsViewer } from "../../../components/datasetsviewer";
import Cookies from "js-cookie";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const DatasetsLocatorPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.datasetsLocatorPage);
  const appearance = useSelector((state) => state.appearance);
  const filter = pageState.filter;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [handleAnimation, setHandleAnimation] = useState(false);
 
  useEffect(() => {
    setTimeout(() => {
      if (handleAnimation) setHandleAnimation(false);
    }, 1500);
  });

  const datasets = pageState.items;

  useEffect(() => {
    if (!datasets || datasets.length === 0) {
      Cookies.set("noDatasets", "true", { expires: 1 });
    } else {
      Cookies.remove("noDatasets");
    }
  }, [datasets]);

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(currentUser, t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.locator.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.locator.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );

    return () => {
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  const handleOnDelete = (referenceKey) => {
    dispatch(
      setAppDialog({
        isVisible: true,
        className: "datasetslocator-page-are-you-sure",
        title: t("datasets.locator.warning"),
        content: t("datasets.locator.areyousure"),
        actions: {
          yesButton: {
            title: t("datasets.locator.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteEntity(referenceKey, t)).then(
                (response) => {},
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("datasets.locator.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(entityDeleted(referenceKey));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessage = t("datasets.locator.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                },
              );
            },
          },
          noButton: { title: t("datasets.locator.no") },
        },
      }),
    );
  };

  const handleOnImport = (id, from) => {
    if (from !== "fromfile") {
      dispatch(importDatasetId(id, t));
    } else {
      navigate(`/importdataset?datasetid=${id}&from=file`, { replace: true });
    }
  };

  return (
    <Fragment>
      <Page
        id="datasetsLocatorPage"
        className="datasetslocator-page"
        title={t("datasets.locator.datasetsview")}
        animation={props.animation}
      >
        <Fragment>
          {pageState.processing && (
            <div className="page-loader">
              <Loader
                size={"large"}
                type="infinite-spinner"
                themeColor={"primary"}
              />
              {pageState.processingText && (
                <div style={{ fontSize: 13, color: currentTheme.primary }}>
                  {pageState.processingText}
                </div>
              )}
            </div>
          )}
          {pageState.pageLoaded && (
            <Fragment>
              <div className="datasetslocator-desktop-view">
                <Splitter
                  style={{ border: "none" }}
                  panes={pageState.panes}
                  onChange={handleOnSplitterChange}
                >
                  <div className="datasetslocator-page-manager">
                    {pageState.canCreate && (
                      <div className="new-dataset">
                        <NavLink to="/createdataset">
                          <div className="new-container" style={{}}>
                            <div>
                              <div className="k-icon k-i-plus"></div>
                              <div>{t("datasets.locator.newdataset")}</div>
                            </div>
                          </div>
                        </NavLink>
                        {pageState.items.length > 0 && (
                          <NavLink to="/importdataset">
                            <div className="new-container" style={{}}>
                              <div>
                                <ImportIcon width={15} />
                                <div>{t("datasets.locator.import")}</div>
                              </div>
                            </div>
                          </NavLink>
                        )}
                      </div>
                    )}
                    <div className="filter-dataset">
                      <div
                        style={{
                          marginTop: 4,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Input
                          onChange={(e) => {
                            setHandleAnimation(true);
                            dispatch(
                              setPageValue({ id: "filter", value: e.value }),
                            );
                          }}
                          value={filter}
                          type="search"
                          autoFocus={true}
                          style={{ flex: 1, padding: 8 }}
                          placeholder={t("datasets.locator.search")}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={`right ${handleAnimation ? "fade" : null}`}>
                    <div className="dataset-locator-wrapper">
                      <div className="title">
                        {t("datasets.locator.datasetsview")}
                      </div>
                      <DatasetsViewer
                        canView={pageState.canView}
                        canCreate={pageState.canCreate}
                        canEdit={pageState.canEdit}
                        canDelete={pageState.canDelete}
                        onDelete={handleOnDelete}
                        onImport={handleOnImport}
                      />
                    </div>
                  </div>
                </Splitter>
              </div>
              <div className="datasetslocator-mobile-view">
                <div className="datasetslocator-mobile-heading">
                  <h1>{t("datasets.locator.datasetsview")}</h1>
                </div>
                <Input
                  onChange={(e) =>
                    dispatch(setPageValue({ id: "filter", value: e.value }))
                  }
                  value={filter}
                  type="search"
                  placeholder={t("datasets.locator.search")}
                  className="datasetslocator-mobile-search"
                />
                <div className="datasetslocator-mobile-list">
                  <DatasetsViewer
                    canView={pageState.canView}
                    canCreate={pageState.canCreate}
                    canEdit={pageState.canEdit}
                    canDelete={pageState.canDelete}
                    onDelete={handleOnDelete}
                    onImport={handleOnImport}
                  />
                </div>
              </div>
            </Fragment>
          )}
        </Fragment>
      </Page>
      <style>
        {`
                    /*page class*/
                    .datasetslocator-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding}; 
                    }

                    .datasetslocator-wrapper{ 
                        direction:${
                          appearance.isRightToLeft ? "rtl" : "ltr"
                        }                      
                    }    
                    .datasetslocator-page .k-splitter{
                      flex-direction:${
                        appearance.isRightToLeft ? "row-reverse" : "row"
                      } 
                    }
                      
                    .datasetslocator-page .k-splitter .k-pane{
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                    }

                    .datasetslocator-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .datasetslocator-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                   
                    /*are you sure to delete*/
                    .datasetslocator-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .datasetslocator-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }


                    /*the manager*/
                    .datasetslocator-page > .content .datasetslocator-page-manager
                    {
                        display: flex;
                        flex-direction: column;
                        flex-wrap: nowrap;
                        height: 100%;
                        overflow: hidden;/* auto;*/
                        padding: 0 0 8px 0;
                        border: ${currentTheme.managerBorder};
                        border-radius: ${currentTheme.pageBorderRadius};
                        background-color: ${currentTheme.componentBgFaded};
                    }

                    .datasetslocator-page > .content .datasetslocator-page-manager .k-dropdownlist:focus,
                    .datasetslocator-page > .content .datasetslocator-page-manager .k-input:focus
                    {
                        box-shadow: none;
                    }

                    .datasetslocator-page > .content .datasetslocator-page-manager .k-dropdownlist,
                    .datasetslocator-page > .content .datasetslocator-page-manager .k-input
                    {
                        background-color: transparent;
                        color: ${currentTheme.componentText};
                    }


                    /*new dataset*/
                    .datasetslocator-page .new-dataset 
                    {
                        display: flex;
                        flex-direction:column;
                        justify-content: center;
                        align-items: center;
                        padding: 32px 16px;

                    }

                    .datasetslocator-page .new-dataset a
                    {
                        width: 100%;
                        display: inline-block;
                        color: ${currentTheme.componentText};
                        text-decoration: none;
                        margin-bottom:15px;
                    }

                    .datasetslocator-page .new-dataset .new-container
                    {
                        padding: 16px 16px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border: 1px solid ${currentTheme.separatorColor};
                        border-radius: 8px;
                        color: ${currentTheme.componentText};   
                        opacity: 0.5;
                    }
                    
                    .datasetslocator-page .new-dataset .new-container:hover
                    {
                        cursor: pointer;
                        background-color: ${currentTheme.componentBgFaded};
                        border-color: ${currentTheme.separatorColor};
                        opacity: 1;
                        transition: opacity 1000ms;
                    }

                    .datasetslocator-page .new-dataset .new-container > div
                    {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }                    

                    .datasetslocator-page .new-dataset .new-container .k-icon
                    {
                        color: ${currentTheme.componentText};   
                        font-size: 20px;
                        font-weight: 300;
                        margin-right: 4px;
                    }    
                    
                    .datasetslocator-page .new-dataset .new-container svg
                    {
                        color: ${currentTheme.componentText};   
                        margin-right: ${appearance.isRightToLeft ? "0" : "4px"};
                        margin-left: ${appearance.isRightToLeft ? "4px" : "0"};
                    }                                   

                    .datasetslocator-page .filter-dataset
                    {
                        display: flex;
                        flex-direction: column;
                        margin-top: 0px;
                        padding: 32px 16px;
                        border-top: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                    }

                    .datasetslocator-page .filter-dataset .k-input::placeholder
                    {
                        color: ${currentTheme.componentText};
                        font-style: italic;
                    }

                    .datasetslocator-page .filter-dataset .k-button
                    {
                        background-color: transparent;
                        border-color: ${currentTheme.separatorColor};
                        border-radius: 50%;
                    }

                    .datasetslocator-page .filter-dataset .k-button .k-icon
                    {
                        color: ${currentTheme.componentText};
                    }

                    .datasetslocator-page .filter-dataset .k-button:hover
                    {
                        background-color: ${currentTheme.separatorColor};
                    }

                    .datasetslocator-page .fav-dataset
                    {
                        display: flex;
                        flex-direction: column;
                        padding: 32px 16px 16px 16px;
                        height: 100%;
                        overflow-y: hidden;
                        border-top: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                    }

                    .datasetslocator-page .fav-dataset .title
                    {
                        color: ${currentTheme.componentText};
                        font-weight: 400;
                        font-size: 16px;
                        margin-bottom: 6px;
                        margin-top: 0px;
                        padding-bottom: 6px;
                        border-bottom: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                    }

                    /*fav list*/
                    .datasetslocator-page .fav-dataset .list-container
                    {
                        /*border: 1px dashed ${currentTheme.separatorColor};*/
                        border-radius: 6px;
                        padding: 0;
                        overflow-y: auto;
                    }

                    .datasetslocator-page .fav-dataset .list-root
                    {
                        border-spacing: 0 6px;/*vertical space between 2 rows*/
                    }

                    .datasetslocator-page .fav-dataset .list-root .list-item
                    {
                    }

                    .datasetslocator-page .fav-dataset .fav-datasets .list-item .list-item-img
                    {
                        
                    }
                    .datasetslocator-page .fav-dataset .fav-datasets .list-item .list-item-img img
                    {
                        width: 40px;
                        height: auto;
                        aspect-ratio: 4 / 3;
                        border-radius: 6px;
                    }

                    .datasetslocator-page .fav-dataset .fav-datasets .list-item .list-item-title
                    {
                        width: 100%;
                        vertical-align: middle;/*fine tuning*/
                    }
                    
                    .datasetslocator-page .fav-dataset .list-root .list-item-view-dataset a,
                    .datasetslocator-page .fav-dataset .list-root .list-item-edit-dataset a                    
                    {                
                        padding: 4px;
                        /*border: 1px solid ${
                          currentTheme.componentBorderColor
                        };
                        border-radius: 4px;*/
                        border: none;
                        display: flex;
                        align-items: center;
                        color: ${currentTheme.touchColor}!important;
                        text-decoration: none;
                    } 

                    .datasetslocator-page .fav-dataset .list-root .list-item-remove            
                    {                                       
                        color: ${currentTheme.error}!important;
                    } 

                    .datasetslocator-page .fav-dataset .fav-datasets .list-item-remove:hover
                    {
                        color: ${currentTheme.touchColor};
                        cursor: pointer;
                    }

                    .datasetslocator-page .right                    
                    {
                        background-color: transparent; 
                        padding-left: 0px;
                        border: ${currentTheme.pageFormBorder};   
                        border-radius: ${currentTheme.pageBorderRadius};
                        display: flex;
                        flex-direction: column;
                    }                    

                    .datasetslocator-page .right.fade
                    {
                        animation-name: hide-dataset;
                        animation-duration: 2s;
                    }

                    @keyframes hide-dataset {
                        0%   {opacity:0};}
                    }

                    .datasetslocator-page .dataset-locator-wrapper{
                    }

                    .datasetslocator-page .dataset-locator-wrapper .title
                    {
                        font-size: 17px;
                        font-weight: 700;
                        color: ${currentTheme.touchColor};
                        padding: 6px 16px 6px 16px;
                        font-style: italic;
                        border-bottom: ${currentTheme.pageFormBorder};
                    }

                    .datasetslocator-desktop-view {
                      flex: 0 0 100%;
                      width: 100%;
                      min-width: 0;
                      height: 100%;
                    }

                    .datasetslocator-desktop-view .k-splitter {
                      width: 100%;
                      height: 100%;
                    }

                    .datasetslocator-mobile-view {
                      display: none;
                    }

                    @media (max-width: 1024px) {
                      .datasetslocator-page > .content {
                        overflow-y: auto;
                      }

                      .datasetslocator-desktop-view {
                        display: none;
                      }

                      .datasetslocator-mobile-view {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        min-height: 100%;
                        padding: 18px 16px;
                        box-sizing: border-box;
                        background: ${currentTheme.componentBg};
                        color: ${currentTheme.componentText};
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"};
                      }

                      .datasetslocator-mobile-heading h1 {
                        margin: 0 0 18px;
                        color: ${currentTheme.componentText};
                        font-size: 21px;
                        font-weight: 600;
                      }

                      .datasetslocator-page .datasetslocator-mobile-search {
                        margin-bottom: 12px;
                        background: transparent;
                      }

                      .datasetslocator-mobile-list {
                        width: 100%;
                        min-width: 0;
                      }
                    }

                    `}
      </style>
    </Fragment>
  );
};
